import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPinIcon, CalendarIcon, UsersIcon, ArrowRightIcon, TagIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { regionDatabase, getStatusStyles } from '../data/bookingData';
export function RegionalBookingPage() {
  const {
    region
  } = useParams<{
    region: string;
  }>();
  const [activeFilter, setActiveFilter] = useState<string>('All Sports');
  const regionKey = region?.toLowerCase() || '';
  const data = regionDatabase[regionKey];
  if (!data) {
    return <main className="min-h-[70vh] flex items-center justify-center bg-navy-50 px-4 pt-20" data-id="element-1131">
        <div className="text-center max-w-lg" data-id="element-1132">
          <h1 className="text-4xl font-bold text-navy-900 mb-4" data-id="element-1133">
            Region Not Found
          </h1>
          <p className="text-lg text-navy-600 mb-8" data-id="element-1134">
            We couldn't find booking information for this region.
          </p>
          <Link to="/book" data-id="element-1135">
            <Button variant="primary" className="inline-flex items-center" data-id="element-1136">
              <ArrowRightIcon className="w-4 h-4 mr-2 rotate-180" data-id="element-1137" />
              Back to Locations
            </Button>
          </Link>
        </div>
      </main>;
  }
  const allSports = Array.from(new Set(data.venues.flatMap(venue => venue.sessions.map(session => session.sport)))).sort();
  return <main className="bg-white pt-20" data-id="element-1138">
      {/* Intro Section */}
      <section className="relative overflow-hidden bg-brand-blue pt-16 pb-12 md:pt-20 md:pb-16" data-id="element-1139">
        <img src="https://cdn.magicpatterns.com/uploads/8m3ZDfLxFh7GqHfUAhVAku/Adults-Badminton-Photo-TK-LK-scaled-e1737371933423_(1).jpg" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-1140" />
        <div className="absolute inset-0 bg-brand-blue/85" data-id="element-1141"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1142">
          <nav aria-label="Breadcrumb" className="flex text-sm font-medium text-white/80 mb-6" data-id="element-1143">
            <Link to="/" className="hover:text-white transition-colors" data-id="element-1144">
              Home
            </Link>
            <span className="mx-2" data-id="element-1145">/</span>
            <Link to="/book" className="hover:text-white transition-colors" data-id="element-1146">
              Book Now
            </Link>
            <span className="mx-2" data-id="element-1147">/</span>
            <span className="text-white" data-id="element-1148">{data.name}</span>
          </nav>

          <div className="max-w-3xl" data-id="element-1149">
            <span className="block text-sm font-bold tracking-widest uppercase mb-3 text-brand-yellow" data-id="element-1150">
              Book Now
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-4 leading-tight uppercase" data-id="element-1151">
              {data.name} Workshops &amp; Classes
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed" data-id="element-1152">
              {data.description}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-gray-50" data-id="element-1153">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1154">
          {/* Filter Row & Change Location */}
          <div className="mb-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1155">
            {allSports.length > 1 ? <div className="flex flex-wrap gap-3 items-center" data-id="element-1156">
                <span className="text-sm font-bold text-navy-900 uppercase tracking-wider mr-2" data-id="element-1157">
                  Filter:
                </span>
                <button onClick={() => setActiveFilter('All Sports')} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors border ${activeFilter === 'All Sports' ? 'bg-navy-900 text-white border-navy-900' : 'bg-white text-navy-600 border-gray-300 hover:border-navy-400'}`} data-id="element-1158">
                  All Sports
                </button>
                {allSports.map(sport => <button key={sport} onClick={() => setActiveFilter(sport)} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors border ${activeFilter === sport ? 'bg-navy-900 text-white border-navy-900' : 'bg-white text-navy-600 border-gray-300 hover:border-navy-400'}`} data-id="element-1159">
                    {sport}
                  </button>)}
              </div> : <div data-id="element-1160" />}
            <Link to="/book" className="text-sm font-medium text-brand-red hover:text-red-800 underline underline-offset-4 transition-colors shrink-0" data-id="element-1161">
              Change location
            </Link>
          </div>

          {/* Venues and Listings */}
          <div className="space-y-10" data-id="element-1162">
            {data.venues.map(venue => {
            const filteredSessions = venue.sessions.filter(session => activeFilter === 'All Sports' || session.sport === activeFilter);
            if (filteredSessions.length === 0) return null;
            return <div key={venue.id} className="bg-white rounded-lg border-2 border-navy-200 overflow-hidden" data-id="element-1163">
                  {/* Venue Header */}
                  <div className="bg-navy-50 border-b border-gray-200 px-6 md:px-8 py-5" data-id="element-1164">
                    <h2 className="text-xl font-display font-bold text-navy-900 flex items-center" data-id="element-1165">
                      <MapPinIcon className="w-5 h-5 text-brand-red mr-3 shrink-0" data-id="element-1166" />
                      {venue.name}
                    </h2>
                    <p className="text-navy-500 text-sm mt-1 ml-8" data-id="element-1167">
                      {venue.address}
                    </p>
                  </div>

                  {/* Sessions List */}
                  <div className="divide-y divide-gray-100" data-id="element-1168">
                    {filteredSessions.map(session => <div key={session.id} className="px-6 md:px-8 py-5 flex flex-col md:flex-row md:items-center justify-between gap-5" data-id="element-1169">
                        {/* Session Info */}
                        <div className="flex-1" data-id="element-1170">
                          <div className="flex flex-wrap items-center gap-3 mb-2" data-id="element-1171">
                            <h3 className="text-base font-bold text-navy-900" data-id="element-1172">
                              {session.title}
                            </h3>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-navy-50 text-navy-700 border border-navy-100" data-id="element-1173">
                              {session.sport}
                            </span>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm text-navy-600" data-id="element-1174">
                            <div className="flex items-center" data-id="element-1175">
                              <UsersIcon className="w-4 h-4 mr-2 text-navy-400" data-id="element-1176" />
                              {session.ageGroup}
                            </div>
                            <div className="flex items-center" data-id="element-1177">
                              <CalendarIcon className="w-4 h-4 mr-2 text-navy-400" data-id="element-1178" />
                              {session.dayTime}
                            </div>
                          </div>
                          {session.pricing && <div className="flex items-center text-sm font-semibold text-navy-900 mt-2" data-id="element-1179">
                              <TagIcon className="w-4 h-4 mr-2 text-brand-yellow" data-id="element-1180" />
                              {session.pricing}
                            </div>}
                        </div>

                        {/* Status and Action */}
                        <div className="flex flex-col sm:flex-row md:flex-col items-start sm:items-center md:items-end gap-3 md:w-44 shrink-0" data-id="element-1181">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusStyles(session.status)}`} data-id="element-1182">
                            {session.status}
                          </span>
                          <Button href={session.status === 'Sold out' ? undefined : `/book/${regionKey}/${session.id}`} variant={session.status === 'Sold out' ? 'secondary' : 'primary'} size="sm" className="w-full sm:w-auto md:w-full" disabled={session.status === 'Sold out'} data-id="element-1183">
                            {session.status === 'Join waiting list' ? 'Join Waitlist' : session.status === 'Sold out' ? 'Sold Out' : 'Book Now'}
                          </Button>
                        </div>
                      </div>)}
                  </div>
                </div>;
          })}

            {/* Empty state — no venues/sessions */}
            {data.venues.length === 0 || data.venues.every(v => v.sessions.length === 0) ? <div className="bg-white rounded-lg border-2 border-navy-200 px-6 md:px-8 py-12 text-center" data-id="element-1184">
                <h3 className="text-xl font-display font-bold text-navy-900 mb-3" data-id="element-1185">
                  No sessions available right now
                </h3>
                <p className="text-navy-600 mb-6 max-w-md mx-auto" data-id="element-1186">
                  We don't currently have any scheduled sessions in {data.name},
                  but we're always expanding. If you're interested in classes in
                  this area, please get in touch.
                </p>
                <Button onClick={() => document.getElementById('contact')?.scrollIntoView({
              behavior: 'smooth'
            })} variant="primary" data-id="element-1187">
                  Contact Us
                </Button>
              </div> : null}
          </div>
        </div>
      </section>
    </main>;
}